/**
 * @file Controlled drug transaction modal routing and fields.
 */

import { appData } from '../../app/state/state.js';
import { formModalElement } from '../../shared/forms/modalShell.js';
import {
    getLocalDateString,
} from "../../shared/utils/dateUtils.js";
import {
    roundToPrecision,
    sortDrugsByOrder,
    sortLocationsByOrder,
    sortUsersByDrugOrder,
} from "./drugUtils.js";
import {
    createSelectControl,
} from "../../shared/ui/controls/selectControl.js";
import {
    applyDrugControlSurface,
    bindDrugNativeFieldSurface,
    DRUG_ICON_BUTTON_CLASSES,
    DRUG_MODAL_AUTO_TEXTAREA_CLASSES,
    DRUG_MODAL_GRID_CLASSES,
    DRUG_MODAL_INPUT_CLASSES,
    DRUG_MODAL_SECTION_HEADING_CLASSES,
    DRUG_READ_ONLY_VALUE_CLASSES,
    getDrugNativeFieldHtml,
    validateDrugNativeFields,
} from "./drugControlStyles.js";

const DRUG_ITEM_INPUT_CLASSES = `${DRUG_MODAL_INPUT_CLASSES} text-sm`;

function createDrugSelectOptions(options, labelResolver, extraResolver = null) {
    return options.map((option) => {
        const label = labelResolver(option);
        return {
            value: option.id,
            label: String(label || 'Unknown'),
            source: option,
            ...(typeof extraResolver === 'function' ? extraResolver(option) : {}),
        };
    });
}

function createDrugItemSelectOptions(inventory, excludedDrugIds = new Set(), currentValue = '') {
    return createDrugSelectOptions(
        inventory.filter((drug) => {
            const drugId = String(drug.id);
            return drugId === String(currentValue || '') || !excludedDrugIds.has(drugId);
        }),
        (drug) => drug.shortName || drug.name || 'Unknown',
    );
}

function hiddenMirrorHtml({ id = '', name = '', className = '', required = false }) {
    const idAttr = id ? ` id="${id}"` : '';
    const nameAttr = name ? ` name="${name}"` : '';
    const classAttr = className ? ` class="${className}"` : '';
    return `<input type="hidden"${idAttr}${nameAttr}${classAttr}${required ? ' required' : ''}>`;
}

function selectSlotHtml({ id = '', name = '', slot, required = false, className = '' }) {
    return `
        ${hiddenMirrorHtml({ id, name, required })}
        <div data-drug-select="${slot}" class="${className}"></div>
    `;
}

/**
 * Generates fields for a controlled drug transaction modal.
 * @param {'use'|'relocate'|'restock'|'correction'|'loss'} type Transaction type.
 * @returns {string} Transaction form markup.
 */
export function getDrugTransactionFormFields(type) {
    const isUse = type === 'use';
    const isRelocate = type === 'relocate';
    const today = getLocalDateString();

    let fieldsHtml = `
        <div class="${DRUG_MODAL_GRID_CLASSES}">
            ${getDrugNativeFieldHtml(
        'Date',
        `<input type="date" name="transactionDate" class="${DRUG_MODAL_INPUT_CLASSES}" value="${today}" required>`,
        { required: true },
    )}
    `;

    // Location Field (Use, Restock, Correction, Loss - single location per transaction)
    if (type === 'use' || type === 'restock' || type === 'correction' || type === 'loss') {
        fieldsHtml += `
            ${getDrugNativeFieldHtml(
        'Location',
        selectSlotHtml({
            id: 'select-location',
            name: 'location',
            slot: 'location',
            required: true,
        }),
        { required: true },
    )}
        `;
    }

    // Patient/Bulk Field (Use only)
    if (isUse) {
        fieldsHtml += `
            ${getDrugNativeFieldHtml(
        'Patient Name',
        `<input type="text" name="patient" id="input-patient" class="${DRUG_MODAL_INPUT_CLASSES}" required>`,
        { required: true, className: '', id: 'patient-field-container' },
    ).replace('<div class="', '<div id="patient-field-container" class="')}
            <div id="bulk-code-container" class="hidden min-w-0 space-y-1">
                <label class="block text-base text-gray-500 sm:text-sm">Bulk Code</label>
                <input type="text" name="bulkCode" id="input-bulk-code" class="${DRUG_READ_ONLY_VALUE_CLASSES} w-full border-0 !bg-gray-100" readonly data-native-field-skip="true">
            </div>
    `;
    }

    // Provider Field (Use and Relocate) - Use users with role_provider
    if (isUse || isRelocate) {
        fieldsHtml += `
            ${getDrugNativeFieldHtml(
        'Provider',
        selectSlotHtml({
            name: 'provider',
            slot: 'provider',
            required: true,
        }),
        { required: true },
    )}
        `;
    }

    // Order Number and Supplier (Restock)
    if (type === 'restock') {
        fieldsHtml += `
            ${getDrugNativeFieldHtml(
        'Supplier',
        selectSlotHtml({
            name: 'supplier',
            slot: 'supplier',
        }),
    )}
            ${getDrugNativeFieldHtml(
        'Order/Invoice Number',
        `<input type="text" name="orderNumber" class="${DRUG_MODAL_INPUT_CLASSES}" required>`,
        { required: true },
    )}
    `;
    }

    // Relocate Locations (on same line)
    if (isRelocate) {
        fieldsHtml += `
            ${getDrugNativeFieldHtml(
        'From Location',
        selectSlotHtml({
            id: 'select-from-location',
            name: 'fromLocation',
            slot: 'fromLocation',
            required: true,
        }),
        { required: true },
    )}
            ${getDrugNativeFieldHtml(
        'To Location',
        selectSlotHtml({
            id: 'select-to-location',
            name: 'toLocation',
            slot: 'toLocation',
            required: true,
        }),
        { required: true },
    )}
    `;
    }

    fieldsHtml += `</div>`;

    // Items Section - Table format with headers
    fieldsHtml += `
        <h4 class="${DRUG_MODAL_SECTION_HEADING_CLASSES} mt-6">Drug Items</h4>
        <div class="drug-transaction-items-wrapper mt-4 overflow-x-auto">
            <table class="drug-transaction-table ${type === 'use' ? 'drug-transaction-table--use' : ''} w-full">
                <thead class="drug-transaction-table-head">
                    <tr class="border-b dark:border-gray-600">
                        <th class="text-left text-sm font-medium text-gray-700 dark:text-gray-300 py-2" style="width: ${type === 'use' ? '236px' : '220px'};">Drug</th>
                        ${type === 'use' ? '<th class="text-left text-sm font-medium text-gray-700 dark:text-gray-300 py-2" style="width: 94px; padding-left: 14px; padding-right: 2px;"></th><th class="text-center text-sm font-medium text-gray-700 dark:text-gray-300 py-2" style="width: 72px; padding-left: 4px; padding-right: 4px;">Amps</th><th class="text-left text-sm font-medium text-gray-700 dark:text-gray-300 py-2" style="width: 110px; padding-left: 4px; padding-right: 8px;"><span class="inline-block text-center" style="width: 68px;">Dose</span></th><th class="text-center text-sm font-medium text-gray-700 dark:text-gray-300 py-2" style="width: 70px; padding-left: 6px; padding-right: 8px;">Total</th>' : '<th class="text-left text-sm font-medium text-gray-700 dark:text-gray-300 py-2">Quantity</th>'}
                        <th class="text-left text-sm font-medium text-gray-700 dark:text-gray-300 py-2" style="width: ${type === 'use' ? '28px' : '40px'};"></th>
                    </tr>
                </thead>
                <tbody id="drug-items-container"></tbody>
            </table>
        </div>
        <button type="button" id="btn-add-item" class="mt-3 inline-flex h-8 items-center justify-center rounded-md px-2 text-sm font-medium text-gray-500 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)]">
            <span class="material-symbols-outlined mr-1 text-[18px] leading-none">add</span>
            Add Drug
        </button>
    `;

    // Note: Justification is now per-drug (handled in addItemRow), not transaction-level

    return fieldsHtml;
}

function mountDrugSelect({
    formElement,
    slot,
    name,
    options,
    placeholder,
    required = false,
    value = '',
    mirrorSelector = null,
    onChange = null,
}) {
    const root = formElement.querySelector(`[data-drug-select="${slot}"]`);
    if (!root) {return null;}
    const mirrorInput = mirrorSelector ?
        formElement.querySelector(mirrorSelector) :
        root.previousElementSibling;
    const control = createSelectControl({
        root,
        name,
        value,
        options,
        placeholder,
        clearable: false,
        required,
        searchable: options.length >= 7,
        mirrorInput,
        onChange: (nextValue) => {
            if (typeof onChange === 'function') {
                onChange(nextValue, mirrorInput);
            }
        },
    });
    if (typeof onChange === 'function') {
        onChange(control.value, mirrorInput);
    }
    return control;
}

function mountDrugTransactionSelectControls(type, formElement) {
    const isUse = type === 'use';
    const isRelocate = type === 'relocate';
    const locations = sortLocationsByOrder(
        (appData.config?.drugs?.locations || []).filter((location) =>
            location.isActive !== false,
        ),
    );
    const suppliers = (appData.config?.drugs?.suppliers || [])
        .filter((supplier) => supplier.isActive !== false)
        .sort((a, b) => (a.order || 99) - (b.order || 99));
    const providerUsers = sortUsersByDrugOrder(
        (appData?.users || []).filter((user) =>
            user.isProvider && user.isActive !== false,
        ),
    );
    const locationOptions = createDrugSelectOptions(locations, (location) => location.name);

    if (type === 'use' || type === 'restock' || type === 'correction' || type === 'loss') {
        mountDrugSelect({
            formElement,
            slot: 'location',
            name: 'location',
            options: locationOptions,
            placeholder: 'Select location',
            required: true,
            mirrorSelector: '#select-location',
        });
    }

    if (isUse || isRelocate) {
        mountDrugSelect({
            formElement,
            slot: 'provider',
            name: 'provider',
            options: createDrugSelectOptions(
                providerUsers,
                (user) => user.name || user.email || 'Unknown',
            ),
            placeholder: 'Select provider',
            required: true,
        });
    }

    if (type === 'restock') {
        mountDrugSelect({
            formElement,
            slot: 'supplier',
            name: 'supplier',
            options: createDrugSelectOptions(suppliers, (supplier) => supplier.name),
            placeholder: 'Select supplier',
            value: suppliers[0]?.id || '',
        });
    }

    if (isRelocate) {
        mountDrugSelect({
            formElement,
            slot: 'fromLocation',
            name: 'fromLocation',
            options: locationOptions,
            placeholder: 'Select location',
            required: true,
            mirrorSelector: '#select-from-location',
        });
        mountDrugSelect({
            formElement,
            slot: 'toLocation',
            name: 'toLocation',
            options: locationOptions,
            placeholder: 'Select location',
            required: true,
            mirrorSelector: '#select-to-location',
        });
    }
}

function mountDrugItemSelect(row, inventory, options = {}) {
    const root = row.querySelector('[data-drug-row-select="drug"]');
    const mirrorInput = row.querySelector('.input-drug');
    if (!root || !mirrorInput) {return null;}
    const currentValue = mirrorInput.value || '';
    return createSelectControl({
        root,
        name: 'drugItem',
        options: createDrugItemSelectOptions(
            inventory,
            options.excludedDrugIds || new Set(),
            currentValue,
        ),
        placeholder: 'Select drug',
        required: true,
        searchable: inventory.length >= 7,
        portalMenu: true,
        mirrorInput,
        onChange: (drugId) => {
            const selectedDrug = inventory.find((drug) =>
                String(drug.id) === String(drugId),
            );
            mirrorInput.dataset.strength = selectedDrug?.strength || '';
            mirrorInput.dataset.unit = selectedDrug?.strengthUnit || '';
            mirrorInput.dataset.shortname = selectedDrug?.shortName || selectedDrug?.name || '';
            mirrorInput.dataset.description = selectedDrug?.description || selectedDrug?.name || '';
            if (typeof options.onChange === 'function') {
                options.onChange(drugId, row);
            }
        },
    });
}

/**
 * Opens the controlled drug transaction modal.
 * @param {'use'|'relocate'|'restock'|'correction'|'loss'} type Transaction type.
 * @returns {void}
 */
export function openDrugForm(type) {
    if (!formModalElement) {
        console.error('Form modal element not found');
        return;
    }

    const formTitle = formModalElement.querySelector('#form-title');
    const formFields = formModalElement.querySelector('#form-fields');
    const formHeaderActions = formModalElement.querySelector('#form-header-actions');
    const itemForm = formModalElement.querySelector('#item-form');

    if (!formTitle || !formFields || !itemForm) {
        console.error('Required form elements not found');
        return;
    }

    // Set form metadata
    const typeLabels = {
        use: 'Log Drug Use',
        relocate: 'Relocate Stock',
        restock: 'Restock',
        correction: 'Manual Correction',
        loss: 'Record Loss',
    };

    formTitle.textContent = typeLabels[type] || 'Drug Transaction';
    applyDrugControlSurface(formModalElement, appData);
    applyDrugControlSurface(itemForm, appData);
    const footer = formModalElement.querySelector('#cancel-form-btn')?.parentElement ||
        formModalElement.querySelector('button[type="submit"]')?.parentElement;
    if (footer) {
        footer.className = 'mt-3 flex flex-wrap justify-end gap-3 px-2 shrink-0';
    }
    formTitle.parentElement?.querySelector('#bulk-checkbox-container')?.remove();
    if (formHeaderActions) {
        formHeaderActions.innerHTML = '';
    }

    // Add bulk checkbox to title area (top right) for use transactions
    // Add bulk checkbox to title area (top right) for use transactions
    // Only if enabled in config
    if (type === 'use' && appData.config?.drugs?.bulkUseEnabled) {
        if (formHeaderActions) {
            const bulkContainer = document.createElement('div');
            bulkContainer.id = 'bulk-checkbox-container';
            bulkContainer.className = 'shrink-0';
            bulkContainer.innerHTML = `
        <label class="flex cursor-pointer items-center gap-2 whitespace-nowrap dark:text-gray-300">
            <input type="checkbox" name="isBulk" id="chk-bulk" class="sr-only">
            <span class="inline-flex h-5 w-5 items-center justify-center rounded border border-gray-300 bg-white text-transparent transition-colors" data-bulk-checkbox-marker>
                <span class="material-symbols-outlined text-[15px] leading-none">check</span>
            </span>
            <span class="text-sm text-gray-600 dark:text-gray-400">Bulk Use</span>
            </label>
    `;
            formHeaderActions.appendChild(bulkContainer);
        }
    } else {
        // Remove bulk checkbox if switching to non-use type
        const existingBulk = formHeaderActions?.querySelector('#bulk-checkbox-container');
        if (existingBulk) {existingBulk.remove();}
    }

    formFields.innerHTML = '';
    itemForm.dataset.type = `drug_${type}`;
    itemForm.dataset.id = '';
    itemForm.dataset.parentId = '';

    // Generate form HTML
    const formHtml = getDrugTransactionFormFields(type);
    formFields.innerHTML = formHtml;
    mountDrugTransactionSelectControls(type, itemForm);
    bindDrugNativeFieldSurface(itemForm);

    // Setup form logic after DOM is ready
    requestAnimationFrame(() => {
        setupDrugFormLogic(type, itemForm);
    });

    formModalElement.classList.add('flex');
}

function setupDrugFormLogic(type, formElement) {
    const container = document.getElementById('drug-items-container');
    const addBtn = document.getElementById('btn-add-item');
    const chkBulk = document.getElementById('chk-bulk');
    const isRelocate = type === 'relocate';
    const inventory = sortDrugsByOrder(
        appData.drugInventory?.filter((d) => d.isActive !== false) || []
    );
    const _locations = sortLocationsByOrder((appData.config?.drugs?.locations || []).filter(l => l.isActive !== false));
    const rowDrugControls = new Map();

    if (!container || !addBtn) {return;}

    const getDrugRows = () =>
        Array.from(container.querySelectorAll('.drug-row'))
            .filter((row) => row.querySelector('.input-drug'));

    const getSelectedDrugIds = (excludedRow = null) =>
        new Set(getDrugRows()
            .filter((row) => row !== excludedRow)
            .map((row) => row.querySelector('.input-drug')?.value)
            .filter(Boolean)
            .map((value) => String(value)));

    const setRowDrugInvalid = (row, invalid) => {
        const root = row.querySelector('[data-drug-row-select="drug"]');
        if (!root) {return;}
        root.toggleAttribute('data-control-invalid', Boolean(invalid));
        root.setAttribute('aria-invalid', String(Boolean(invalid)));
    };

    const updateDrugItemSelectOptions = () => {
        getDrugRows().forEach((row) => {
            const control = rowDrugControls.get(row);
            const mirrorInput = row.querySelector('.input-drug');
            if (!control || !mirrorInput) {return;}
            control.update({
                options: createDrugItemSelectOptions(
                    inventory,
                    getSelectedDrugIds(row),
                    mirrorInput.value,
                ),
            });
        });
    };

    const mountRowDrugControl = (row) => {
        const control = mountDrugItemSelect(row, inventory, {
            excludedDrugIds: getSelectedDrugIds(row),
            onChange: () => {
                updateDrugItemSelectOptions();
            },
        });
        if (control) {
            rowDrugControls.set(row, control);
        }
        updateDrugItemSelectOptions();
        return control;
    };

    const removeRowDrugControl = (row) => {
        const control = rowDrugControls.get(row);
        if (control) {
            control.destroy();
            rowDrugControls.delete(row);
        }
    };

    // --- Global: Date/Time Input Color Logic (For Drug Form) ---
    const dateInputs = formElement.querySelectorAll('input[type="date"], input[type="datetime-local"]');
    dateInputs.forEach(input => {
        const updateColor = () => {
            if (!input.value) {
                input.classList.add('text-gray-500', 'dark:text-gray-400');
                input.classList.remove('text-gray-900', 'dark:text-gray-200');
            } else {
                input.classList.remove('text-gray-500', 'dark:text-gray-400');
                input.classList.add('text-gray-900', 'dark:text-gray-200');
            }
        };
        input.addEventListener('change', updateColor);
        input.addEventListener('input', updateColor);
        updateColor();
    });

    // Add first row by default
    const addItemRow = () => {
        if (type === 'use') {
            // Use transactions: 3 rows per drug
            // Row 1: Drug + Total (rowspan=2 for both)
            const row1 = document.createElement('tr');
            row1.className = 'drug-row drug-use-row drug-use-row--primary';
            row1.innerHTML = `
                <td class="drug-use-drug-cell border-b py-2 align-middle dark:border-gray-600" rowspan="2" style="vertical-align: middle; width: 236px;">
                    ${hiddenMirrorHtml({ className: 'input-drug', required: true })}
                    <div data-drug-row-select="drug"></div>
                </td>
                <td class="drug-use-label-cell drug-use-admin-label-cell pb-1 pt-2 align-middle" style="vertical-align: middle; width: 94px; padding-left: 14px; padding-right: 2px;">
                    <span class="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">Administered</span>
                </td>
                <td class="drug-use-amps-cell drug-use-admin-amps-cell pb-1 pt-2 align-middle" style="vertical-align: middle; width: 72px; padding-left: 4px; padding-right: 4px;">
                    <input type="number" step="any" class="input-administered-amps drug-compact-number-input ${DRUG_ITEM_INPUT_CLASSES} text-center" min="0" placeholder="0" style="width: 68px; min-width: 68px; max-width: 68px;">
                </td>
                <td class="drug-use-dose-cell drug-use-admin-dose-cell pb-1 pt-2 align-middle" style="vertical-align: middle; width: 110px; padding-left: 4px; padding-right: 8px;">
                    <div class="flex items-center gap-1">
                        <input type="number" step="any" class="input-administered-dose drug-compact-number-input ${DRUG_ITEM_INPUT_CLASSES} text-center" placeholder="0" style="width: 68px; min-width: 68px; max-width: 68px;">
                        <span class="text-xs text-gray-500 dark:text-gray-400 unit-label-administered whitespace-nowrap">mg</span>
                    </div>
                </td>
                <td class="drug-use-total-cell border-b py-2 align-middle dark:border-gray-600" rowspan="2" style="vertical-align: middle; width: 70px; padding-left: 6px; padding-right: 8px;">
                    <div class="flex items-center justify-center">
                        <span class="input-total ${DRUG_READ_ONLY_VALUE_CLASSES} inline-flex w-16 items-center justify-center border border-gray-200 text-center font-medium">0</span>
                    </div>
                     <div class="text-[10px] text-center mt-1 hidden stock-warning text-red-600 font-bold">Insufficient Stock</div>
                </td>
                <td class="drug-use-remove-cell border-b py-2 align-middle dark:border-gray-600" rowspan="2" style="vertical-align: middle; width: 28px; padding-right: 0;">
                    <div class="flex items-center justify-center">
                        <button type="button" class="btn-remove ${DRUG_ICON_BUTTON_CLASSES}" title="Remove" aria-label="Remove drug item">
                            <span class="material-symbols-outlined text-lg">close</span>
                        </button>
                    </div>
                </td>
    `;
            container.appendChild(row1);

            // Row 2: Discarded
            const row2 = document.createElement('tr');
            row2.className = 'drug-row drug-use-row drug-use-row--secondary border-b dark:border-gray-600';
            row2.innerHTML = `
                <td class="drug-use-label-cell drug-use-discard-label-cell pb-2 pt-1 align-middle" style="vertical-align: middle; width: 94px; padding-left: 14px; padding-right: 2px;">
                    <span class="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">Discarded</span>
                </td>
                <td class="drug-use-amps-cell drug-use-discard-amps-cell pb-2 pt-1 align-middle" style="vertical-align: middle; width: 72px; padding-left: 4px; padding-right: 4px;">
                    <input type="number" step="any" class="input-discarded-amps drug-compact-number-input ${DRUG_ITEM_INPUT_CLASSES} text-center" min="0" placeholder="0" style="width: 68px; min-width: 68px; max-width: 68px;">
                </td>
                <td class="drug-use-dose-cell drug-use-discard-dose-cell pb-2 pt-1 align-middle" style="vertical-align: middle; width: 110px; padding-left: 4px; padding-right: 8px;">
                    <div class="flex items-center gap-1">
                        <input type="number" step="any" class="input-discarded-dose drug-compact-number-input ${DRUG_ITEM_INPUT_CLASSES} text-center" placeholder="0" style="width: 68px; min-width: 68px; max-width: 68px;">
                        <span class="text-xs text-gray-500 dark:text-gray-400 unit-label-discarded whitespace-nowrap">mg</span>
                    </div>
                </td>
    `;
            container.appendChild(row2);

            // Store reference to rows
            row1.dataset.drugGroup = 'true';
            row2.dataset.drugGroup = 'true';
            mountRowDrugControl(row1);

            // Setup logic for this drug group
            setupUseDrugRowLogic(row1, row2);

        } else {
            // Non-use transactions: single row
            const rowHtml = document.createElement('tr');
            // Remove bottom border if justification row will be added (no border between them)
            const borderClass = (type === 'correction' || type === 'loss') ? '' : 'border-b dark:border-gray-600';
            rowHtml.className = `drug-row ${borderClass}`;

            // Determine column count for colspan
            const columnCount = type === 'use' ? 6 : 3; // Use has 6 columns, others have 3

            rowHtml.innerHTML = `
                <td class="py-2">
                    ${hiddenMirrorHtml({ className: 'input-drug', required: true })}
                    <div data-drug-row-select="drug"></div>
                </td>
                <td class="py-2">
                    <input type="number" step="any" class="input-qty ${DRUG_ITEM_INPUT_CLASSES}" ${type === 'correction' ? '' : `min="0"`} required>
                    <div class="text-xs text-red-600 font-bold mt-1 hidden stock-warning">Insufficient Stock</div>
                </td>
                <td class="py-2">
                    <button type="button" class="btn-remove ${DRUG_ICON_BUTTON_CLASSES}" title="Remove" aria-label="Remove drug item">
                        <span class="material-symbols-outlined text-lg">close</span>
                    </button>
                </td>
    `;
            container.appendChild(rowHtml);
            mountRowDrugControl(rowHtml);

            // For correction/loss, add justification row below (spans all columns)
            let justificationRow;
            if (type === 'correction' || type === 'loss') {
                justificationRow = document.createElement('tr');
                justificationRow.className = 'justification-row border-b dark:border-gray-600';
                // Store reference to drug row for easy removal
                rowHtml.dataset.rowId = justificationRow.dataset.drugRowId;
                justificationRow.innerHTML = `
                <td colspan="${columnCount}" class="py-2">
                    <textarea class="input-justification ${DRUG_MODAL_AUTO_TEXTAREA_CLASSES}" placeholder="Justification" rows="1" required data-auto-grow="true"></textarea>
                </td>
        `;
                container.appendChild(justificationRow);
            }

            // Setup remove button - remove both drug row and justification row if exists
            const btnRemove = rowHtml.querySelector('.btn-remove');
            if (btnRemove) {
                btnRemove.onclick = () => {
                    // Find justification row BEFORE removing drug row (using next sibling)
                    const nextRow = rowHtml.nextElementSibling;
                    const isJustificationRow = nextRow && nextRow.classList.contains('justification-row');

                    // Remove drug row first
                    removeRowDrugControl(rowHtml);
                    rowHtml.remove();

                    // Then remove justification row if it exists
                    if (isJustificationRow && nextRow.parentNode) {
                        nextRow.remove();
                    }
                    updateDrugItemSelectOptions();
                };
            }
        }
        bindDrugNativeFieldSurface(formElement);

    };

    // Helper: Check Stock Level
    const checkStock = (drugId, locationId, qty) => {
        if (!drugId || !locationId) {return { sufficient: true, current: 0 };}
        const drug = appData.drugInventory?.find(d => d.id === drugId);
        const current = drug?.stockLevels?.[locationId] || 0;
        return { sufficient: current >= qty, current };
    };

    // Setup logic for use transaction drug rows (2-row layout: drug+total, then administered, then discarded)
    const setupUseDrugRowLogic = (row1, row2) => {
        const selDrug = row1.querySelector('.input-drug');
        const inputTotal = row1.querySelector('.input-total');
        const inputAdministeredAmps = row1.querySelector('.input-administered-amps');
        const inputAdministeredDose = row1.querySelector('.input-administered-dose');
        const inputDiscardedAmps = row2.querySelector('.input-discarded-amps');
        const inputDiscardedDose = row2.querySelector('.input-discarded-dose');
        const unitLabelAdministered = row1.querySelector('.unit-label-administered');
        const unitLabelDiscarded = row2.querySelector('.unit-label-discarded');
        const btnRemove = row1.querySelector('.btn-remove');

        // Remove button - removes both rows
        if (btnRemove) {
            btnRemove.onclick = () => {
                removeRowDrugControl(row1);
                row1.remove();
                row2.remove();
                updateDrugItemSelectOptions();
            };
        }

        // Update total function
        const updateTotal = () => {
            const administered = parseFloat(inputAdministeredAmps?.value) || 0;
            const discarded = parseFloat(inputDiscardedAmps?.value) || 0;
            const total = administered + discarded;

            if (inputTotal) {
                inputTotal.textContent = total;

                // Validate whole number
                if (total > 0 && total % 1 !== 0) {
                    inputTotal.classList.add('border-red-500', 'bg-red-50', 'dark:bg-red-900');
                    inputTotal.classList.remove('bg-gray-100', 'dark:bg-gray-800');
                } else {
                    inputTotal.classList.remove('border-red-500', 'bg-red-50', 'dark:bg-red-900');
                    inputTotal.classList.add('bg-gray-100', 'dark:bg-gray-800');
                }

                // NEW: Stock Validation (Soft Block)
                const locationId = document.getElementById('select-location')?.value;
                const drugId = selDrug?.value;
                const warningEl = row1.querySelector('.stock-warning');

                if (locationId && drugId && warningEl) {
                    const { sufficient, current } = checkStock(drugId, locationId, total);

                    if (!sufficient) {
                        warningEl.textContent = `Insufficient Stock(Max: ${current})`;
                        warningEl.classList.remove('hidden');
                        inputTotal.classList.add('border-orange-500', 'bg-orange-50'); // Warn color
                    } else {
                        warningEl.classList.add('hidden');
                        // Don't remove red border if it was set by whole number validation
                        if (!(total > 0 && total % 1 !== 0)) {
                            inputTotal.classList.remove('border-orange-500', 'bg-orange-50');
                        }
                    }
                }
            }
        };

        // Listen for Location Change (Global for this form) to re-validate
        const locSelect = document.getElementById('select-location');
        if (locSelect) {
            locSelect.addEventListener('change', updateTotal);
        }

        // Drug selection handler
        if (selDrug) {
            selDrug.addEventListener('change', () => {
                if (!selDrug.value) {
                    return;
                }

                const unit = selDrug.dataset.unit || 'mg';

                // Update unit labels
                if (unitLabelAdministered) {unitLabelAdministered.textContent = unit;}
                if (unitLabelDiscarded) {unitLabelDiscarded.textContent = unit;}

                // Re-validate total on drug change
                updateTotal();
            });
        }

        // Converter: Administered Dose -> Amps
        if (inputAdministeredDose && inputAdministeredAmps && selDrug) {
            inputAdministeredDose.addEventListener('input', () => {
                if (!selDrug.value) {return;}
                const strength = parseFloat(selDrug.dataset.strength);
                const dose = parseFloat(inputAdministeredDose.value);
                if (!isNaN(dose) && strength && dose > 0) {
                    inputAdministeredAmps.value = roundToPrecision(dose / strength);
                    updateTotal();
                }
            });

            // Converter: Administered Amps -> Dose
            inputAdministeredAmps.addEventListener('input', () => {
                if (!selDrug.value) {return;}
                const strength = parseFloat(selDrug.dataset.strength);
                const amps = parseFloat(inputAdministeredAmps.value);
                if (!isNaN(amps) && strength && amps > 0) {
                    inputAdministeredDose.value = roundToPrecision(amps * strength);
                }
                updateTotal();
            });
        }

        // Converter: Discarded Dose -> Amps
        if (inputDiscardedDose && inputDiscardedAmps && selDrug) {
            inputDiscardedDose.addEventListener('input', () => {
                if (!selDrug.value) {return;}
                const strength = parseFloat(selDrug.dataset.strength);
                const dose = parseFloat(inputDiscardedDose.value);
                if (!isNaN(dose) && strength && dose > 0) {
                    inputDiscardedAmps.value = roundToPrecision(dose / strength);
                    updateTotal();
                }
            });

            // Converter: Discarded Amps -> Dose
            inputDiscardedAmps.addEventListener('input', () => {
                if (!selDrug.value) {return;}
                const strength = parseFloat(selDrug.dataset.strength);
                const amps = parseFloat(inputDiscardedAmps.value);
                if (!isNaN(amps) && strength && amps > 0) {
                    inputDiscardedDose.value = roundToPrecision(amps * strength);
                }
                updateTotal();
            });
        }
    };

    addBtn.onclick = addItemRow;
    addItemRow(); // Add first row

    // Bulk Use Logic - Replace patient field with bulk code field (readonly, auto-generated)
    if (chkBulk) {
        const syncBulkMarker = () => {
            const marker = formModalElement.querySelector('[data-bulk-checkbox-marker]');
            if (!marker) {return;}
            marker.style.backgroundColor = chkBulk.checked ? 'var(--dfc-brand-primary)' : '';
            marker.style.borderColor = chkBulk.checked ? 'var(--dfc-brand-primary)' : '#d1d5db';
            marker.style.color = chkBulk.checked ? 'var(--dfc-brand-primary-contrast)' : 'transparent';
        };
        const patientContainer = document.getElementById('patient-field-container');
        const bulkCodeContainer = document.getElementById('bulk-code-container');

        // Generate a preview bulk code (Server determines final code, this is the likely next one)
        const _generatePreviewBulkCode = () => {
            // Counter relies on backend triggers; show placeholder
            return `CS-[Auto-Generated]`;
        };

        chkBulk.onchange = async () => {
            syncBulkMarker();
            const patientInput = patientContainer?.querySelector('input') || patientContainer?.querySelector('select'); // Attempt to find the input

            if (chkBulk.checked) {
                // Hide patient field, show bulk code field
                if (patientContainer) {
                    patientContainer.classList.add('hidden');
                    if (patientInput) {patientInput.required = false;} // Disable required when hidden
                }
                if (bulkCodeContainer) {
                    bulkCodeContainer.classList.remove('hidden');
                    const bulkCodeInput = bulkCodeContainer.querySelector('#input-bulk-code');
                    if (bulkCodeInput) {
                        // Show placeholder while loading
                        bulkCodeInput.value = 'Loading...';
                        try {
                            const { getNextBulkCode } = await import('./drugApi.js');
                            const code = await getNextBulkCode();
                            // Check if still checked and input still exists
                            if (chkBulk.checked && bulkCodeInput) {
                                bulkCodeInput.value = code || 'Error';
                            }
                        } catch (e) { void e;
                            if (chkBulk.checked && bulkCodeInput) {
                                bulkCodeInput.value = 'Error';
                            }
                        }
                    }
                }
            } else {
                // Show patient field, hide bulk code field
                if (patientContainer) {
                    patientContainer.classList.remove('hidden');
                    if (patientInput) {patientInput.required = true;} // Re-enable required when shown
                }
                if (bulkCodeContainer) {
                    bulkCodeContainer.classList.add('hidden');
                }
            }
            bindDrugNativeFieldSurface(formElement);
        };
        syncBulkMarker();

        // If bulk is already checked on load, trigger the change handler
        if (chkBulk.checked) {
            chkBulk.dispatchEvent(new Event('change'));
        }
    }

    // Relocate validation - ensure from and to are different
    if (isRelocate) {
        const fromSelect = document.getElementById('select-from-location');
        const toSelect = document.getElementById('select-to-location');

        if (fromSelect && toSelect) {
            const validateLocations = () => {
                if (fromSelect.value && toSelect.value && fromSelect.value === toSelect.value) {
                    toSelect.setCustomValidity('From and To locations must be different');
                    toSelect.classList.add('border-red-500');
                } else {
                    toSelect.setCustomValidity('');
                    toSelect.classList.remove('border-red-500');
                }
            };

            fromSelect.onchange = validateLocations;
            toSelect.onchange = validateLocations;
        }
    }

    // Relocate/Loss/Correction - Single Row Validation
    // Multi-Row Validation (Duplicates & Stock)
    if (container) {
        const validateForm = () => {
            const rows = Array.from(container.querySelectorAll('tr'));
            const submitBtn = formElement.querySelector('button[type="submit"]');
            let hasError = false;

            // 1. Collect Drug Counts (across all rows)
            const drugCounts = {};
            rows.forEach(row => {
                const drugId = row.querySelector('.input-drug')?.value;
                if (drugId) {
                    drugCounts[drugId] = (drugCounts[drugId] || 0) + 1;
                }
            });

            // 2. Validate each logical 'item' (might be 1 or 2 rows)
            rows.forEach(row => {
                // If this is the second row of a 'use' group, skip it (will be handled by first row)
                if (type === 'use' && !row.querySelector('.input-drug')) {return;}

                const drugInput = row.querySelector('.input-drug');
                // For 'use' type, quantity is in input-total span; for others in input-qty input
                const qtyInput = type === 'use' ? row.querySelector('.input-total') : row.querySelector('.input-qty');
                const warningEl = row.querySelector('.stock-warning');

                if (!drugInput || !qtyInput) {return;}

                const drugId = drugInput.value;
                // Parse textContent for span (use), value for input (others)
                const qtyValue = type === 'use' ? qtyInput.textContent : qtyInput.value;
                const qty = parseFloat(qtyValue) || 0;

                let rowError = false;
                let warningMsg = '';

                // Reset Styles
                drugInput.classList.remove('border-red-500', 'bg-red-50');
                setRowDrugInvalid(row, false);
                if (type !== 'use') {
                    qtyInput.classList.remove('border-orange-500', 'bg-orange-50');
                } else {
                    // Span styling reset handled by its own logic, but we'll clear warning El
                }

                // A. Duplicate Check
                if (drugId && drugCounts[drugId] > 1) {
                    drugInput.classList.add('border-red-500', 'bg-red-50');
                    setRowDrugInvalid(row, true);
                    rowError = true;
                }

                // B. Stock Check (Only check if no duplicate error to avoid clutter)
                if (!rowError && drugId && qty !== 0) {
                    // Determine source location for stock check
                    let checkLocationId = null;
                    if (type === 'use' || type === 'loss') {
                        checkLocationId = document.getElementById('select-location')?.value;
                    } else if (type === 'relocate') {
                        checkLocationId = document.getElementById('select-from-location')?.value;
                    } else if (type === 'correction' && qty < 0) {
                        // For corrections, only check stock if we are reducing it
                        checkLocationId = document.getElementById('select-location')?.value;
                    }
                    // Note: 'restock' doesn't require stock level check

                    if (checkLocationId && typeof checkStock === 'function') {
                        const { sufficient, current } = checkStock(drugId, checkLocationId, Math.abs(qty));
                        if (!sufficient) {
                            if (type !== 'use') {qtyInput.classList.add('border-orange-500', 'bg-orange-50');}
                            warningMsg = `Insufficient Stock (Max: ${current})`;
                            rowError = true;
                        }
                    }
                }

                // Update Warning UI (Only for Stock warnings now)
                if (warningEl) {
                    if (warningMsg) {
                        warningEl.textContent = warningMsg;
                        warningEl.classList.remove('hidden');
                    } else {
                        warningEl.classList.add('hidden');
                    }
                }

                if (rowError) {hasError = true;}
            });

            if (submitBtn) {
                submitBtn.disabled = hasError;
                submitBtn.title = hasError ? 'Please fix errors before submitting' : '';
                if (hasError) {
                    submitBtn.classList.add('opacity-50', 'cursor-not-allowed');
                } else {
                    submitBtn.classList.remove('opacity-50', 'cursor-not-allowed');
                }
            }
        };

        // Event Listeners for Validation
        container.addEventListener('input', (e) => {
            if (e.target.matches('.input-qty') || e.target.matches('.input-drug') ||
                e.target.matches('.input-administered-amps') || e.target.matches('.input-discarded-amps')) {
                // For use forms, we need a tiny delay for the 'updateTotal' calc to run first
                setTimeout(validateForm, 10);
            }
        });

        container.addEventListener('change', (e) => {
            if (e.target.matches('.input-drug')) {
                validateForm();
            }
        });

        // Global Location Changes
        ['select-location', 'select-from-location'].forEach(id => {
            const el = document.getElementById(id);
            if (el) {el.addEventListener('change', validateForm);}
        });

        // Handle Row Removal
        container.addEventListener('click', (e) => {
            if (e.target.closest('.btn-remove')) {
                setTimeout(validateForm, 50);
            }
        });

        // Initial validation
        setTimeout(validateForm, 100);
    }

    // Form Submission Handler - applied to ALL drug transaction types to override default API handler
    formElement.onsubmit = async (e) => {
        e.preventDefault();
        if (!validateDrugNativeFields(formElement)) {return;}
        const { handleDrugFormSubmit } = await import('./renderDrugs.js');
        if (handleDrugFormSubmit) {
            await handleDrugFormSubmit(type, formElement);
        }
    };
}
